import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Link,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { IComment, IJiraIssue } from "../../common/interface";
import { ADFRenderer } from "../ADFRenderer";
import { useAttachmentContext } from "../../context/AttachmentContext";

interface ITreeComment extends IComment {
  replies?: ITreeComment[];
}

const buildCommentTree = (comments: IComment[]): ITreeComment[] => {
  const commentMap = new Map<string, ITreeComment>();
  const rootComments: ITreeComment[] = [];

  // First pass: Add all comments to a map and initialize replies array
  comments.forEach((comment) => {
    commentMap.set(comment.id, { ...comment, replies: [] });
  });

  // Second pass: Organize into parent-child relationships
  comments.forEach((comment) => {
    const currentComment = commentMap.get(comment.id)!;
    if (comment.parentId) {
      // Ensure parentId is treated as string for map key consistency
      const parentComment = commentMap.get(comment.parentId.toString());
      if (parentComment) {
        parentComment.replies?.push(currentComment);
      } else {
        // If parent not found, treat as a root comment
        rootComments.push(currentComment);
      }
    } else {
      // This is a root comment (no parentId)
      rootComments.push(currentComment);
    }
  });

  const sortRepliesRecursively = (comment: ITreeComment) => {
    if (comment.replies && comment.replies.length > 0) {
      comment.replies.sort(
        (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime()
      );
      comment.replies.forEach(sortRepliesRecursively);
    }
  };

  rootComments.sort(
    (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime()
  );
  rootComments.forEach(sortRepliesRecursively);

  return rootComments;
};

const CommentCard: React.FC<{ comment: ITreeComment; level: number }> = ({
  comment,
  level,
}) => {
  const isEdited = comment.created !== comment.updated;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
    }).format(date);
  };

  const [expanded, setExpanded] = useState<string | false>(false);
  const handleChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const timeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    const minutes = Math.floor(diffInSeconds / 60);
    const hours = Math.floor(diffInSeconds / 3600);
    const days = Math.floor(diffInSeconds / 86400);
    const months =
      now.getMonth() -
      date.getMonth() +
      12 * (now.getFullYear() - date.getFullYear());
    const years = now.getFullYear() - date.getFullYear();
    if (years > 0) return `${years} year${years > 1 ? "s" : ""} ago`;
    if (months > 0) return `${months} month${months > 1 ? "s" : ""} ago`;
    if (days > 0) return `${days} day${days > 1 ? "s" : ""} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
    return `${diffInSeconds} second${diffInSeconds > 1 ? "s" : ""} ago`;
  };

  return (
    <Paper variant="outlined" sx={{ padding: 2, marginBottom: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Avatar
          src={comment.author.avatarUrls["48x48"]}
          alt={comment.author.displayName}
          sx={{ width: 36, height: 36 }} // Smaller avatar for comments
        />
        <Box sx={{ marginLeft: 1 }}>
          <Typography variant="subtitle2" component="div">
            <Link
              className="pointer-events-none"
              href={comment.author.self}
              target="_blank"
              underline="hover"
              color="inherit" // Inherit color
              sx={{ fontWeight: "bold" }}
            >
              {comment.author.displayName}
            </Link>
          </Typography>

          <Box sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
            <Tooltip
              placement="top-start"
              slotProps={{
                tooltip: {
                  sx: {
                    backgroundColor: "#fff",
                    color: "#14274e",
                    boxShadow: "0px 2px 6px rgba(0,0,0,0.2)", // Softer shadow
                    padding: "8px 16px",
                    borderRadius: "6px",
                    textAlign: "center",
                    fontSize: "0.8rem",
                  },
                },
              }}
              id={`created_${comment.id}`}
              title={formatDate(comment.created)}
            >
              <span> {timeAgo(comment.created)} </span>
            </Tooltip>
            {isEdited && (
              <Tooltip
                placement="top-start"
                slotProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: "#fff",
                      color: "#14274e",
                      boxShadow: "0px 2px 6px rgba(0,0,0,0.2)",
                      padding: "8px 16px",
                      borderRadius: "6px",
                      textAlign: "center",
                      fontSize: "0.8rem",
                    },
                  },
                }}
                id={`updated_${comment.id}`}
                title={formatDate(comment.updated)}
              >
                <Typography
                  component="span"
                  sx={{ ml: 0.5, fontStyle: "italic" }}
                >
                  (edited)
                </Typography>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>

      {/* Correctly renders rich content */}
      <Box sx={{ mt: 1 }}>
        <ADFRenderer content={comment.body.content} />
      </Box>
      {comment.replies && comment.replies.length > 0 && (
        <Accordion
          onChange={handleChange(`panel-${comment.id}`)} // Handle state change
          sx={{
            border: "none",
            boxShadow: "none", // Remove accordion shadow
            "&:before": { display: "none" }, // Remove default line
            backgroundColor: "transparent",
            ml: "-16px", // Shift left to align 'View Replies' with the line
            width: `calc(100% + ${level > 0 ? "16px" : "0px"})`, // Adjust width for shifted accordion
          }}
        >
          <AccordionSummary
            aria-controls={`panel-${comment.id}-content`}
            id={`panel-${comment.id}-header`}
            sx={{
              "& .MuiAccordionSummary-content": {
                margin: "2px 0", // Adjust content margin
              },
              height: "fit-content",
            }}
          >
            <Typography
              className="cursor-pointer"
              variant="body2"
              sx={{ border: "none" }}
            >
              {expanded === `panel-${comment.id}` ? "Hide " : "View "}
              {comment.replies.length} repl
              {comment.replies.length > 1 ? "ies" : "y"}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {/* Recursively render replies inside accordion details */}
            <CommentsTreeSection
              commentTree={comment.replies}
              level={level + 1}
            />
          </AccordionDetails>
        </Accordion>
      )}
    </Paper>
  );
};

interface CommentsTreeSectionProps {
  commentTree: ITreeComment[];
  level?: number;
}

const CommentsTreeSection: React.FC<CommentsTreeSectionProps> = ({
  commentTree,
  level = 0,
}) => {
  return (
    <Box>
      {commentTree.map((comment) => (
        <Box
          key={comment.id}
          sx={{
            marginLeft: level > 0 ? `${level * 24}px` : 0,
            paddingLeft: level > 0 ? "16px" : 0, // Space between line and content
            marginTop: level === 0 ? "16px" : "12px", // Spacing between root comments and replies
          }}
        >
          <CommentCard comment={comment} level={level} />
        </Box>
      ))}
    </Box>
  );
};

const CommentsSection = ({
  commentDetails,
  issueData,
}: {
  commentDetails: IComment[];
  issueData: IJiraIssue;
}) => {
  // Build the tree structure from the flat comments array
  const commentTree = commentDetails ? buildCommentTree(commentDetails) : [];

  const { setAttachments, setRenderedFields } = useAttachmentContext();
  const renderedFields = issueData?.renderedFields;
  const attachment = issueData?.fields?.attachment;

  useEffect(() => {
    setAttachments(attachment ?? []);
    setRenderedFields(renderedFields ?? null);
  }, [attachment, setAttachments, renderedFields, setRenderedFields]);

  return (
    <Box sx={{ padding: 2 }}>
      {commentTree.length > 0 ? (
        <CommentsTreeSection commentTree={[...commentTree].reverse()} />
      ) : (
        <h3 className="text-2xl text-[#14274e] p-2 text-center">
          No Comment for This Ticket
        </h3>
      )}
    </Box>
  );
};

export default CommentsSection;
